import storage from './storage';
import { supabase, isConfigured } from './supabase';

const CACHE_KEY = 'crm_feed_comments';
const TABLE = 'crm_feed_comments';

let _activeChannel = null;
let _subscriberCount = 0;

function notifyChanged(feedId) {
  try {
    window.dispatchEvent(new CustomEvent('crm-feed-comments-changed', { detail: { feedId } }));
  } catch { /* ignore */ }
}

function rowToComment(row) {
  return {
    id: row.id,
    userId: row.user_id,
    userName: row.user_name || 'Nutzer',
    text: row.body,
    ts: row.created_at,
  };
}

function groupByActivity(rows) {
  const out = {};
  for (const row of rows || []) {
    const fid = row.activity_id;
    if (!fid) continue;
    if (!out[fid]) out[fid] = [];
    out[fid].push(rowToComment(row));
  }
  return out;
}

function mergeGrouped(local, remote) {
  const out = { ...local };
  for (const [fid, list] of Object.entries(remote)) {
    const byId = new Map();
    for (const c of out[fid] || []) byId.set(String(c.id), c);
    for (const c of list) byId.set(String(c.id), c);
    out[fid] = Array.from(byId.values()).sort(
      (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
    );
  }
  return out;
}

export const feedCommentsService = {
  getAll() {
    return storage.get(CACHE_KEY, {});
  },

  setCache(grouped) {
    storage.setLocal(CACHE_KEY, grouped);
  },

  getForActivity(feedId) {
    const all = this.getAll();
    return Array.isArray(all[feedId]) ? all[feedId] : [];
  },

  /** Letzte Kommentare aus Supabase laden (Team-Sync). */
  async pullRecent(days = 14) {
    if (!isConfigured) return this.getAll();
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return this.getAll();

    const since = new Date(Date.now() - days * 86400000).toISOString();
    const { data, error } = await supabase
      .from(TABLE)
      .select('id, activity_id, lead_id, user_id, user_name, body, created_at')
      .gte('created_at', since)
      .order('created_at', { ascending: true })
      .limit(3000);

    if (error) throw error;

    const remote = groupByActivity(data);
    const merged = mergeGrouped(this.getAll(), remote);
    this.setCache(merged);
    notifyChanged('__all__');
    return merged;
  },

  async add(feedId, { userId, userName, text, leadId }) {
    if (!feedId || !text?.trim()) return [];

    const trimmed = text.trim();
    const optimistic = {
      id: `tmp_${Date.now()}`,
      userId: userId || null,
      userName: userName || 'Nutzer',
      text: trimmed,
      ts: new Date().toISOString(),
    };

    const before = this.getAll();
    const nextList = [...(before[feedId] || []), optimistic];
    this.setCache({ ...before, [feedId]: nextList });
    notifyChanged(feedId);

    if (!isConfigured || !leadId) return nextList;

    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return nextList;

    const { data, error } = await supabase
      .from(TABLE)
      .insert({
        activity_id: feedId,
        lead_id: String(leadId),
        user_id: user.id,
        user_name: userName || user.email || 'Nutzer',
        body: trimmed,
      })
      .select('id, activity_id, lead_id, user_id, user_name, body, created_at')
      .single();

    if (error) {
      const rollback = (before[feedId] || []).filter(c => c.id !== optimistic.id);
      this.setCache({ ...before, [feedId]: rollback });
      notifyChanged(feedId);
      throw error;
    }

    const confirmed = rowToComment(data);
    const current = this.getAll();
    const list = (current[feedId] || []).map(c => (c.id === optimistic.id ? confirmed : c));
    this.setCache({ ...current, [feedId]: list });
    notifyChanged(feedId);
    return list;
  },

  /** Realtime: neue Kommentare von anderen Clients. */
  subscribeRealtime() {
    if (!isConfigured) return () => {};

    _subscriberCount += 1;

    if (!_activeChannel) {
      _activeChannel = supabase
        .channel('crm_feed_comments_live')
        .on(
          'postgres_changes',
          { event: 'INSERT', schema: 'public', table: TABLE },
          (payload) => {
            const row = payload.new;
            if (!row?.activity_id) return;
            const fid = row.activity_id;
            const comment = rowToComment(row);
            const all = feedCommentsService.getAll();
            const existing = all[fid] || [];
            if (existing.some(c => String(c.id) === String(comment.id))) return;
            feedCommentsService.setCache({
              ...all,
              [fid]: [...existing, comment].sort(
                (a, b) => new Date(a.ts).getTime() - new Date(b.ts).getTime(),
              ),
            });
            notifyChanged(fid);
          },
        )
        .subscribe();
    }

    return () => {
      _subscriberCount -= 1;
      if (_subscriberCount <= 0) {
        try { supabase.removeChannel(_activeChannel); } catch { /* ignore */ }
        _activeChannel = null;
        _subscriberCount = 0;
      }
    };
  },
};
